import axiosInstance from '../../utils/axiosInstance';
import { AxiosResponse } from 'axios';

// Models
export interface Transaction {
  id: number;
  amount: number;
  transactionType: string;
  dateTime: string;
  type: string;
  notes: string;
}

export interface Payment {
  id: number;
  amount: number;
  paymentDate: string;
  originalStatus: string;
  description: string;
}

export interface DailyEmployeeFund {
  id: number;
  employee: {
    id: number;
    name: string;
  };
  date: string;
  startingBalance: number;
  companyAllocation: number;
  previousDayBalance: number;
  dailyTransactions: Transaction[];
  dailyPayments: Payment[];
}

export interface DailyEmployeeFundSummary extends DailyEmployeeFund {
  totalDeposits: number; // Tổng nạp đáo
  totalWithdrawals: number; // Tổng rút
  totalCustomerFees: number; // Tổng phí thu khách hàng
  balanceWithFees: number; // Quỹ đầu ngày + phí thu khách hàng
  finalBalance: number; // Quỹ đầu ngày - Số tiền nạp đáo + phí thu khách hàng
}

class DailyEmployeeFundService {
  private readonly baseUrl = '/daily-employee-funds';

  /**
   * Tính toán các số liệu tổng hợp cho một bản ghi DailyEmployeeFund
   */
  private calculateSummary(fund: DailyEmployeeFund): DailyEmployeeFundSummary {
    // Đảm bảo các mảng không undefined
    const transactions = fund.dailyTransactions || [];
    const payments = fund.dailyPayments || [];
    const startingBalance = fund.startingBalance || 0;

    // Tính tổng nạp đáo (các giao dịch type = 'DEPOSIT')
    const totalDeposits = transactions
      .filter((t) => t.transactionType?.toUpperCase() === 'DEPOSIT')
      .reduce((sum, t) => sum + (t.amount || 0), 0);

    // Tính tổng rút (các giao dịch type = 'WITHDRAWAL')
    const totalWithdrawals = transactions
      .filter((t) => t.transactionType?.toUpperCase() === 'WITHDRAWAL')
      .reduce((sum, t) => sum + (t.amount || 0), 0);

    // Tính tổng phí thu khách hàng (các payment có status = 'COMPLETED')
    const totalCustomerFees = payments
      .filter((p) => p.originalStatus === 'COMPLETED')
      .reduce((sum, p) => sum + (p.amount || 0), 0);

    // Tính quỹ đầu ngày + phí thu khách hàng
    const balanceWithFees = startingBalance + totalCustomerFees;

    // Tính quỹ đầu ngày - Số tiền nạp đáo + phí thu khách hàng
    const finalBalance = startingBalance - totalDeposits + totalCustomerFees;

    return {
      ...fund,
      totalDeposits,
      totalWithdrawals,
      totalCustomerFees,
      balanceWithFees,
      finalBalance,
    };
  }

  /**
   * Lấy tất cả bản ghi DailyEmployeeFund của một ngày cụ thể với số liệu tổng hợp
   * @param date Ngày cần lấy dữ liệu (format: YYYY-MM-DD)
   */
  async getAllDailyFundsByDate(
    date: string
  ): Promise<DailyEmployeeFundSummary[]> {
    try {
      const response: AxiosResponse<DailyEmployeeFund[]> =
        await axiosInstance.get(`${this.baseUrl}/by-date`, {
          params: { date },
        });

      // Đảm bảo response.data là mảng
      const funds = Array.isArray(response.data) ? response.data : [];
      return funds.map((fund) => this.calculateSummary(fund));
    } catch (error) {
      console.error('Error fetching daily funds by date:', error);
      throw error;
    }
  }

  /**
   * Lấy bản ghi DailyEmployeeFund của một nhân viên theo ngày với số liệu tổng hợp
   */
  async getDailyFundByEmployeeAndDate(
    employeeId: number,
    date: string
  ): Promise<DailyEmployeeFundSummary> {
    try {
      const response: AxiosResponse<DailyEmployeeFund> =
        await axiosInstance.get(`${this.baseUrl}/employee/${employeeId}`, {
          params: { date },
        });
      return this.calculateSummary(response.data);
    } catch (error) {
      console.error('Error fetching daily fund for employee:', error);
      throw error;
    }
  }

  /**
   * Lấy tất cả bản ghi DailyEmployeeFund của một nhân viên trong khoảng thời gian với số liệu tổng hợp
   */
  async getDailyFundsByEmployeeAndDateRange(
    employeeId: number,
    startDate: string,
    endDate: string
  ): Promise<DailyEmployeeFundSummary[]> {
    try {
      const response: AxiosResponse<DailyEmployeeFund[]> =
        await axiosInstance.get(
          `${this.baseUrl}/employee/${employeeId}/date-range`,
          {
            params: {
              startDate,
              endDate,
            },
          }
        );

      // Đảm bảo response.data là mảng
      const funds = Array.isArray(response.data) ? response.data : [];
      return funds.map((fund) => this.calculateSummary(fund));
    } catch (error) {
      console.error('Error fetching daily funds by date range:', error);
      throw error;
    }
  }

  /**
   * Format date to YYYY-MM-DD
   */
  formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
  }
}

export const dailyEmployeeFundService = new DailyEmployeeFundService();
export default dailyEmployeeFundService;
